import AuthenticatedClient from '@/repositories/clients/AuthenticatedClient';

/**
 * In-app feedback reporter (`/api/v2/feedback`).
 *
 * Submission is multipart: the report itself as a JSON `payload` field and the
 * annotated screenshots as file parts. Attachments are uploaded here and only
 * here — the capture and annotation steps stay entirely in the browser.
 */
export default {
  /** `{ can_submit }` — the server's verdict on the explicit per-user allowlist. */
  capability() {
    return AuthenticatedClient.get('feedback/capability');
  },

  /**
   * @param {object} payload   FeedbackSubmitRequest (see the backend schema)
   * @param {Blob[]} screenshots Annotated images, already redacted and cropped
   */
  submit(payload, screenshots = []) {
    const form = new FormData();
    form.append('payload', JSON.stringify(payload));
    screenshots.forEach((blob, index) => {
      const extension = (blob.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
      form.append('screenshots', blob, `screenshot-${index + 1}.${extension}`);
    });
    return AuthenticatedClient.post('feedback/reports', form);
  },

  /** The caller's own reports. Authenticated, not allowlisted: losing submit
   *  access must not hide the reports already filed, or our replies. */
  myReport(reportId) {
    return AuthenticatedClient.get(`feedback/reports/${encodeURIComponent(reportId)}`);
  },
  myReports(params = {}) {
    return AuthenticatedClient.get('feedback/reports', { params });
  },

  /** Reword your own report. The server refuses once it has been picked up. */
  updateMyReport(reportId, description) {
    return AuthenticatedClient.patch(`feedback/reports/${encodeURIComponent(reportId)}`, {
      description,
    });
  },

  /** Withdraw your own report. The server takes its screenshots with it. */
  deleteMyReport(reportId) {
    return AuthenticatedClient.delete(`feedback/reports/${encodeURIComponent(reportId)}`);
  },

  /** Answer a reviewer on your own report, so neither side has to quote a
   *  reference code at the other. */
  replyToMyReport(reportId, body) {
    return AuthenticatedClient.post(`feedback/reports/${encodeURIComponent(reportId)}/messages`, {
      body,
    });
  },

  /** Screenshots are behind the same auth as the report, so they are fetched
   *  as blobs — an `<img src>` would reach the API without the bearer token. */
  myAttachment(reportId, attachmentId) {
    return AuthenticatedClient.get(
      `feedback/reports/${encodeURIComponent(reportId)}/attachments/${encodeURIComponent(attachmentId)}`,
      { responseType: 'blob' }
    );
  },
};
